/**
 * Meta Pixel Utility
 * Provides reliable tracking with retry mechanism
 */

const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || '1143568807149570';

// Track if pixel has been initialized
let pixelInitialized = false;

/**
 * Initialize Meta Pixel if not already done
 * Useful for dynamic environments
 */
export const initMetaPixel = () => {
  if (typeof window === 'undefined') return;
  
  // Check if already initialized
  if (window.fbq && pixelInitialized) return;
  
  // Check if fbq exists but wasn't initialized by us
  if (window.fbq) {
    pixelInitialized = true;
    return;
  }
  
  // Initialize pixel manually if script hasn't loaded
  // Note: textContent is used instead of innerHTML to avoid XSS risk (CWE-79)
  const script = document.createElement('script');
  script.textContent = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${META_PIXEL_ID}');
    fbq('track', 'PageView');
  `;
  document.head.appendChild(script);
  pixelInitialized = true;
};

/**
 * Track a Meta Pixel event with retry mechanism
 * @param {string} eventName - Event name (e.g., 'Purchase', 'Subscribe', 'CompleteRegistration')
 * @param {object} params - Event parameters
 * @param {number} retries - Number of retries if fbq not ready
 */
export const trackEvent = (eventName, params = {}, retries = 3) => {
  if (typeof window === 'undefined') {
    console.log(`[MetaPixel] Server-side render, skipping: ${eventName}`);
    return;
  }
  
  const attemptTrack = (attemptsLeft) => {
    if (window.fbq) {
      try {
        window.fbq('track', eventName, params);
        console.log(`[MetaPixel] Event tracked: ${eventName}`, params);
      } catch (e) {
        console.error(`[MetaPixel] Error tracking ${eventName}:`, e);
      }
    } else if (attemptsLeft > 0) {
      // Retry after a short delay
      console.log(`[MetaPixel] fbq not ready, retrying ${eventName} in 500ms...`);
      setTimeout(() => attemptTrack(attemptsLeft - 1), 500);
    } else {
      console.warn(`[MetaPixel] fbq not available after retries, event not tracked: ${eventName}`);
      // Try to initialize and track
      initMetaPixel();
      if (window.fbq) {
        window.fbq('track', eventName, params);
        console.log(`[MetaPixel] Event tracked after init: ${eventName}`, params);
      }
    }
  };
  
  attemptTrack(retries);
};

/**
 * Track CompleteRegistration event with value-based intent scoring
 * @param {number} value - 0 for low intent (skip/timeout), 20 for high intent (completed action)
 */
export const trackSignup = (value = 0) => {
  trackEvent('CompleteRegistration', {
    currency: 'INR',
    value: value,
    content_name: 'User Signup'
  });
};

/**
 * Track Subscribe event for plan subscriptions
 * @param {string} planName - Plan name (e.g., 'Starter Plan', 'Premium Plan')
 * @param {number} value - Subscription value in rupees
 */
export const trackSubscribe = (planName, value) => {
  trackEvent('Subscribe', {
    currency: 'INR',
    value: value,
    content_name: planName
  });
};

/**
 * Track Purchase event for one-time purchases
 * @param {string} productName - Product name
 * @param {number} value - Purchase value in rupees
 */
export const trackPurchase = (productName, value) => {
  trackEvent('Purchase', {
    currency: 'INR',
    value: value,
    content_name: productName,
    content_type: 'product'
  });
};

export default {
  initMetaPixel,
  trackEvent,
  trackSignup,
  trackSubscribe,
  trackPurchase
};
